// BRIEF 101 §1 — THE DATE BLOCK, ONCE.
//
// Three files rendered the same four class names in the same three lines —
// `CaseEventRow`, `CaseTaskRow` and `Dashboard` — and 101 §0's ruling was about
// to make it five. Extracting first is the difference between applying a rule
// and multiplying a copy.
//
// ── WHAT IT OWNS, AND WHAT IT DOES NOT ──────────────────────────────────────
//
// It owns the SHAPE: a wrapper and three lines, in that order, with those
// classes. It does NOT own the empty state. §1a: "`CaseEventRow` renders an
// em-dash when there is no date and the caller may edit, `""` when it may not,
// and `CaseTaskRow` renders `"—"` — those are the caller's business, not the
// block's."
//
// So this component is TOTAL over a real date and renders nothing else: a
// caller with no date renders its own thing, and the two callers that have an
// empty state keep the two different ones they already had. An `empty` prop
// could not have expressed them anyway — `CaseEventRow`'s empty state is the
// wrapper plus a muted day plus an empty month line (which occupies 10px and is
// part of why the card does not jump), and `CaseTaskRow`'s is a bare `—` with
// no wrapper at all. One prop, two shapes, and the byte-identity §1b requires
// would have been the first casualty.
//
// ── THE DAY IS THE INTERACTIVE LINE, AND THE COLOURED ONE ───────────────────
//
// `CaseEventRow` hangs its click-to-edit on the DAY rather than the wrapper, and
// brief 101 §4b puts the Dashboard's overdue red there too — "the day is the
// thing that is late". Both arrive as props rather than being decided here:
// §4c is explicit that the block renders whatever it is handed, so it neither
// computes overdue-ness nor reaches for a task's own `dueDate`.
import type { CSSProperties, MouseEventHandler } from "react";
import { pm } from "../utils";

type DateBlockProps = {
  /**
   * Anything `new Date()` accepts. REQUIRED: absence is the caller's business,
   * so there is no "no date" branch in here to get wrong.
   */
  date: string | number | Date;
  /**
   * §4b — a colour for the DAY line only. The caller passes a token it already
   * uses; nothing is invented here and no second token was needed.
   */
  dayColor?: string;
  /** `CaseEventRow`'s click-to-edit, which has always lived on the day. */
  onDayClick?: MouseEventHandler<HTMLDivElement>;
  dayTitle?: string;
  /** Wrapper-level, for the hosts that make the whole block the click target. */
  className?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLDivElement>;
  title?: string;
};

export default function DateBlock({
  date,
  dayColor,
  onDayClick,
  dayTitle,
  className,
  style,
  onClick,
  title,
}: DateBlockProps) {
  const d = new Date(date);
  return (
    <div
      className={className ? `date-block ${className}` : "date-block"}
      style={style}
      onClick={onClick}
      title={title}
    >
      <div
        className="date-day"
        style={{
          ...(onDayClick ? { cursor: "pointer" } : {}),
          ...(dayColor ? { color: dayColor } : {}),
        }}
        onClick={onDayClick}
        title={dayTitle}
      >
        {d.getDate()}
      </div>
      <div className="date-month">{pm(date)}</div>
      <div className="date-year">{d.getFullYear()}</div>
    </div>
  );
}
